<template>
    <validation-observer ref="digital_signature_modal" v-slot="{ meta, setFieldValue, errors }" as="span">
        <modal
            :id="modalId"
            title="Digital Signature Request"
            scrollable
            @show="fetchAllData"
            :ok-disabled="!meta.valid || busy"
            @ok.prevent="sendRequest"
        >
            <validation-provider name="to_email" rules="email|required">
                <form-input
                    floatingLabel="To:"
                    v-model="to_email"
                    :error="errors.to_email"
                    @input="(val) => setFieldValue('to_email', val)"
                />
            </validation-provider>
            <validation-provider name="subject" rules="required">
                <form-input
                    floatingLabel="Subject:"
                    v-model="subject"
                    :error="errors.subject"
                    @input="(val) => setFieldValue('subject', val)"
                />
            </validation-provider>
            <form-select
                floatingLabel="Rental Agreement:"
                :options="rentalAgreementOptions"
                v-model="selectedAgreementOption"
            />
            <div class="d-flex align-items-start w-100 flex-wrap flex-md-nowrap">
                <form-select
                    class="flex-grow-1"
                    :options="emailsOptions"
                    v-model="selectedEmailOption"
                    floatingLabel="Email Response"
                />
                <b-button
                    class="ml-md-2 mb-3 mb-md-0 flex-grow-1 pt-2 d-flex align-items-center justify-content-center"
                    style="padding-bottom: 0.65rem"
                    variant="primary"
                    @click="userCanUseUnlayer ? applyUnlayerResponse() : applyResponse()"
                >
                    Apply
                </b-button>
            </div>
            <lx-editor v-model="message" v-if="!isText"/>
            <form-textarea v-if="isText" floating-label="Message" v-model="message" rows="6" />
        </modal>
    </validation-observer>
</template>

<script>
import API from '../../api';
import {
    APP_EMAIL_RESPONSE_SIGNATURES,
    APP_SEND_SIGNATURE_REQUEST,
    DOCUMENT_REPORT_TEMPLATE_AGREEMENTS_ENDPOINT,
    UNLAYER_TEMPLATE_BY_ID_ENDPOINT,
    APP_EMAIL_MOCKUP_RESPONSES,
} from '../../endpoints';
import FormInput from '../generic/form-input/form-input.vue';
import FormSelect from '../generic/form-select/form-select.vue';
import FormTextarea from '../generic/form-textarea/form-textarea.vue';
import modal from '../generic/modal/modal.vue';
import LxEditor from '../generic/lx-editor/lx-editor.vue';
import { mapGetters } from 'vuex';

export default {
    name: 'DigitalSignatureRequestModal',
    components: { modal, FormInput, FormSelect, FormTextarea, LxEditor },
    props: {
        initialValues: Object,
        modalId: String,
        onUpdate: {
            type: Function,
            default: () => {},
        },
    },
    computed: {
        ...mapGetters('header', ['userCanUseUnlayer']),
        isText() {
            return this.initialValues.airbnb_messaging_api || this.initialValues.vrbo_messaging_api;
        },
    },
    data() {
        return {
            editorModules: {
                modules: {
                    toolbar: {
                        container: [
                            ['bold', 'italic', 'underline', 'strike'],
                            ['blockquote', 'code-block'],
                            [{ header: 1 }, { header: 2 }],
                            ['clean'],
                            [{ list: 'ordered' }, { list: 'bullet' }],
                        ],
                    },
                },
            },
            rentalAgreementOptions: [],
            emailsOptions: [],
            message: null,
            subject: 'Please sign this rental agreement',
            selectedAgreementOption: null,
            selectedEmailOption: null,
            to_email: null,
            busy: false,
        };
    },
    methods: {
        fetchAllData() {
            if (this.userCanUseUnlayer) {
                this.fetchUnlayerData();
            } else {
                this.fetchData();
            }
        },
        async applyResponse() {
            try {
                const res = await API.get(APP_EMAIL_MOCKUP_RESPONSES(this.selectedEmailOption, this.initialValues.id));
                this.message = res.data;
            } catch (e) {
                console.log(e);
                this.errorToast({ text: 'Could not fetch data' });
            }
        },
        async applyUnlayerResponse() {
            try {
                const res = await API.post(
                    UNLAYER_TEMPLATE_BY_ID_ENDPOINT(this.selectedEmailOption) + 'apply-template/',
                    {
                        meta_reservation_id: this.initialValues.id || 0,
                    }
                );
                this.message = res.data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            }
        },
        async fetchUnlayerData() {
            try {
                const response = await API.get(UNLAYER_TEMPLATE_BY_ID_ENDPOINT() + 'agreements/');
                this.rentalAgreementOptions = [
                    {
                        value: null,
                        text: 'Send reservation default agreement(s)',
                    },
                    ...response.data.map((item) => ({
                        value: item.id,
                        text: item.name,
                    })),
                ];
                const emailResponse = await API.get(UNLAYER_TEMPLATE_BY_ID_ENDPOINT() + 'signature-responses/');
                this.emailsOptions = emailResponse.data.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));
                const defaultItem = emailResponse.data.find((item) => item.is_default);
                this.selectedEmailOption = defaultItem ? defaultItem.id : '';
                this.to_email = this.initialValues.user.email;
                await this.applyUnlayerResponse();
                this.$refs.digital_signature_modal.setValues({
                    to_email: this.to_email,
                    subject: this.subject,
                });
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            }
        },
        async fetchData() {
            try {
                const response = await API.get(DOCUMENT_REPORT_TEMPLATE_AGREEMENTS_ENDPOINT());
                this.rentalAgreementOptions = [
                    {
                        value: null,
                        text: 'Send reservation default agreement(s)',
                    },
                ].concat(
                    response.data.map((item) => ({
                        value: item.id,
                        text: item.report_name,
                    }))
                );

                const emailResponse = await API.get(APP_EMAIL_RESPONSE_SIGNATURES());
                this.emailsOptions = emailResponse.data.map((item) => ({
                    value: item.id,
                    text: item.title,
                }));
                const defaultItem = emailResponse.data.find((item) => item.is_default_request_digital_signature_email);
                this.selectedEmailOption = defaultItem ? defaultItem.id : '';
                this.to_email = this.initialValues.user.email;
                await this.applyResponse();
                this.$refs.digital_signature_modal.setValues({
                    to_email: this.to_email,
                    subject: this.subject,
                });
            } catch (e) {
                this.errorToast({ text: 'Could not fetch data' });
            }
        },
        async sendRequest() {
            try {
                this.busy = true;
                await API.post(APP_SEND_SIGNATURE_REQUEST(this.initialValues.id), {
                    to_email: this.to_email,
                    message: this.message,
                    subject: this.subject,
                    template_id: this.selectedAgreementOption,
                });
                this.$bvModal.hide(this.modalId);
                this.onUpdate();
                this.successToast({ text: 'Signature successfully requested' });
            } catch (e) {
                this.errorToast({ text: 'Could not send request' });
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>
